import React, { useEffect, useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  History,
  Loader2,
  Trash2,
} from "lucide-react";

const ScoreBoxForReading = ({ practiceItemId, onIsAttempted }) => {
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const fetchResponses = async () => {
      try {
        const response = await fetch(`/api/get-user-responses/${practiceItemId}`);
        if (!response.ok) {
          throw new Error("Failed to fetch responses");
        }

        const responseData = await response.json();
        setResponses(responseData);

        if (responseData.length > 0) {
          onIsAttempted(responseData[0].isAttempted);
        }
      } catch (error) {
        console.error("Error fetching responses:", error);
      } finally {
        setLoading(false);
      }
    };

    if (practiceItemId) {
      fetchResponses();
    }
  }, [onIsAttempted, practiceItemId]);

  const deleteResponse = (id) => {
    const updatedResponses = responses.filter((response) => response.id !== id);
    setResponses(updatedResponses);
  };

  const highlightMatchingWords = (userResponse, remarks) => {
    const words = userResponse.split(" ");
    const tone =
      remarks === "Correct answer"
        ? "bg-green-50 text-green-700 ring-green-100"
        : remarks === "Incorrect answer"
        ? "bg-red-50 text-red-700 ring-red-100"
        : "bg-slate-50 text-gray-700 ring-slate-100";

    return words.map((word, index) => (
      <span
        key={`${word}-${index}`}
        className={`mb-1 mr-1 rounded-md px-2 py-1 text-xs font-semibold ring-1 ${tone}`}
      >
        {word}
      </span>
    ));
  };

  return (
    <div className="rounded-md border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-purple-50 text-purple-700">
            <History className="h-5 w-5" aria-hidden="true" />
          </span>
          <div>
            <p className="text-sm font-semibold uppercase text-purple-700">
              History
            </p>
            <h2 className="text-lg font-extrabold text-gray-900">
              Your responses
            </h2>
          </div>
        </div>

        <button
          type="button"
          onClick={() => setIsOpen((current) => !current)}
          className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-slate-50 text-gray-500 transition hover:bg-purple-50 hover:text-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-100"
          aria-label={isOpen ? "Collapse responses" : "Expand responses"}
        >
          {isOpen ? (
            <ChevronUp className="h-5 w-5" aria-hidden="true" />
          ) : (
            <ChevronDown className="h-5 w-5" aria-hidden="true" />
          )}
        </button>
      </div>

      {isOpen && (
        <div className="mt-4">
          {loading && (
            <div className="flex min-h-24 items-center justify-center rounded-md bg-slate-50 text-sm font-semibold text-gray-500">
              <Loader2 className="mr-2 h-4 w-4 animate-spin text-purple-700" />
              Loading responses
            </div>
          )}

          {!loading && responses.length === 0 && (
            <div className="rounded-md bg-slate-50 p-4 text-center text-sm text-gray-500">
              No responses yet.
            </div>
          )}

          {!loading && responses.length > 0 && (
            <div className="space-y-4">
              {responses.map((response, index) => (
                <article
                  key={response.id || `${response.updatedAt}-${index}`}
                  className="rounded-md border border-slate-200 bg-slate-50 p-3"
                >
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <div>
                      <p className="text-xs font-semibold uppercase text-gray-500">
                        Attempt {index + 1}
                      </p>
                      <p className="text-sm font-bold text-gray-900">
                        {new Date(response.updatedAt).toLocaleDateString(
                          "en-GB"
                        )}
                      </p>
                    </div>
                    <button
                      type="button"
                      className="grid h-9 w-9 place-items-center rounded-md bg-white text-gray-400 shadow-sm transition hover:bg-red-50 hover:text-red-600"
                      onClick={() => deleteResponse(response.id)}
                      aria-label="Delete response"
                    >
                      <Trash2 className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div className="rounded-md bg-white p-3 shadow-sm">
                      <p className="text-xs font-semibold uppercase text-gray-500">
                        Score
                      </p>
                      <p className="mt-1 text-lg font-extrabold text-gray-900">
                        {response.totalScore ?? "N/A"}
                      </p>
                    </div>
                    <div className="rounded-md bg-white p-3 shadow-sm">
                      <p className="text-xs font-semibold uppercase text-gray-500">
                        Result
                      </p>
                      <p className="mt-1 text-sm font-bold text-gray-900">
                        {response.remarks || "N/A"}
                      </p>
                    </div>
                  </div>

                  <div className="mt-3 rounded-md bg-white p-3 shadow-sm">
                    <p className="text-xs font-semibold uppercase text-gray-500">
                      User response
                    </p>
                    <div className="mt-2 flex flex-wrap">
                      {highlightMatchingWords(
                        response.userResponse || "No detailed response yet.",
                        response.remarks
                      )}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ScoreBoxForReading;
